import React, { useCallback } from 'react';
import { useSearchParams, useNavigate } from 'react-router-dom';
import { InteractiveContentCard } from '@amzn/seller-university-ui-components';
import { KatPagination } from '@amzn/katal-react';
import { SEARCH_PARAM } from 'src/constants/search';
import { useBundle } from '@amzn/react-arb-tools';
import { t } from 'src/utils/translation';
import { BUNDLE_NAME, SEARCH_STRING_ID } from 'src/constants/localization';
import { useSearchContentQuery } from 'src/redux/slices/api/su';
import { useClientId, useSelLocale } from 'src/contexts/ClientContext';
import { SearchItem } from 'src/types/api/searchResults';
import { FeatureName } from 'src/metrics/metricsFields';
import useMetrics from 'src/metrics/useUserMetrics';
import usePagePerformance from 'src/metrics/usePagePerformance';
import { API_SEARCH_PARAMS } from 'src/constants/api';
import { ROUTE_SEARCH_PARAM } from 'src/constants/route';
import LoadingView from 'src/components/LoadingView';
import ErrorView from 'src/components/ErrorView';
import { getStatusCodeFromError } from 'src/utils/error';
import { LearningModuleModal } from 'src/components/learning-module';
import { EntityType } from 'src/constants/entity';
import { removeSearchParam } from 'src/utils/searchParams';

const SearchPage = () => {
  const [searchBundle] = useBundle(BUNDLE_NAME.SEARCH);
  const [searchParams, setSearchParams] = useSearchParams();
  const navigate = useNavigate();
  const currentPage = parseInt(searchParams.get(API_SEARCH_PARAMS.PAGE_NUMBER) || '1', 10);

  const queryParam = searchParams.get(API_SEARCH_PARAMS.QUERY);

  // legacy search param backward compatibility
  const searchTextParam = searchParams.get(API_SEARCH_PARAMS.SEARCH_TEXT);

  const searchQuery = queryParam || searchTextParam || '';

  const learningModuleId = searchParams.get(ROUTE_SEARCH_PARAM.LEARNING_MODULE_ID);
  const itemsPerPage = SEARCH_PARAM.PAGE_SIZE;
  const { clickMetric } = useMetrics();
  const clientId = useClientId();
  const selLocale = useSelLocale();

  const {
    data: searchData, isLoading, isError, error: searchError,
  } = useSearchContentQuery({
    query: searchQuery,
    pageNumber: (currentPage - 1) * SEARCH_PARAM.PAGE_SIZE,
    clientId,
    selLocale,
  });

  const removeModuleSearchParam = useCallback(() => {
    navigate({ search: removeSearchParam(searchParams, ROUTE_SEARCH_PARAM.LEARNING_MODULE_ID) }, { replace: true });
  }, [navigate, searchParams]);

  // Move useMemo hooks to top level to ensure consistent hook order
  const searchResultsWithCount = React.useMemo(
    () => (
      <p className="text-right">
        {searchData && t(searchBundle, SEARCH_STRING_ID.SEARCH_RESULTS_COUNT, {
          number: searchData.totalCount,
          userSearchQuery: searchQuery,
        })}
      </p>
    ),
    [searchBundle, searchData, searchQuery],
  );

  // Track per-page performance (latency + availability). A missing payload when not loading
  // renders the error view, so it counts as a fault alongside an explicit query error.
  // SearchPage lives on a single stable route (/learn/s) and re-queries whenever the query or
  // page-number search param changes without remounting, so pass the search identity as the
  // resource key to reset the measurement and re-emit metrics for each new search.
  usePagePerformance(
    FeatureName.SearchPage,
    isLoading,
    isError || !searchData,
    JSON.stringify({ q: searchQuery, page: currentPage }),
  );

  if (isLoading) return <LoadingView />;
  if (isError || !searchData) {
    return <ErrorView statusCode={getStatusCodeFromError(searchError)} />;
  }

  /* istanbul ignore next */
  const handlePageChange = (event: CustomEvent<{ page: number }>) => {
    setSearchParams((prev) => {
      prev.set(API_SEARCH_PARAMS.PAGE_NUMBER, event.detail.page.toString());
      return prev;
    });
  };

  return (
    <div className="p-su-space-05" data-testid="search-page">
      {/* Search result counts */}
      {searchResultsWithCount}

      {/* Cards Grid */}
      <div className="my-su-space-06 grid grid-cols-1 gap-x-su-space-04 gap-y-su-space-08
      sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-4"
      >
        {searchData.results.map((item: SearchItem, index: number) => (
          <InteractiveContentCard
            key={item.resourceId}
            imageSrc={item.thumbnailLink}
            contentVariant={item.contentVariant!}
            title={item.title}
            metadata={item.metadata!}
            previewDescription={item.description}
            layout="vertical"
            onClick={() => {
              clickMetric({
                feature: FeatureName.LearningModuleCard,
                metadata: {
                  moduleTitle: item.title,
                  resourceId: item.resourceId,
                  pageNumber: currentPage,
                  positionOnPage: index + 1,
                },
              });

              const updatedSearchParams = new URLSearchParams(searchParams);
              updatedSearchParams.set(ROUTE_SEARCH_PARAM.LEARNING_MODULE_ID, item.resourceId);
              navigate({ search: updatedSearchParams.toString() }, { replace: true });
            }}
          />
        ))}
      </div>

      {/* Pagination */}
      <KatPagination
        className="flex justify-center"
        itemsPerPage={itemsPerPage}
        page={currentPage}
        totalItems={searchData.totalCount}
        onPageChanged={handlePageChange}
      />

      {learningModuleId && (
        <LearningModuleModal
          learningModuleId={learningModuleId}
          onModalClose={removeModuleSearchParam}
          parentType={EntityType.SearchPage}
          parentId={learningModuleId} // Required in backend, using moduleId as it has no parent on search page and this may ease BI analysis.
        />
      )}
    </div>
  );
};

export default SearchPage;
